/**
 * src/lib/data/markets.ts — Phase 5.5d markets catalog client.
 *
 * Pulls the list of countries from the worker (`/api/markets/catalog`),
 * which in turn reads HubSpot's `country_of_origin` enum. Used by the
 * AdminUsers form so admins pick from real CRM countries instead of
 * typing free-form slugs.
 *
 * Sibling of `market.ts` (which is for per-market settings — the Market
 * entity, still a stub). The "catalog" is a small read-only list; the
 * "market" entity is the operational forecast settings record.
 */

export interface MarketCatalogEntry {
  /** Lowercase slug, matches the worker's marketIds regex `[a-z0-9_-]+`. */
  slug: string;
  /** Human display label as-is from HubSpot ("Italy", "United States"). */
  label: string;
}

export interface MarketCatalogOptions {
  workerUrl: string;
  viewPassword: string;
  /** Force a HubSpot re-fetch (bypasses worker KV cache). */
  refresh?: boolean;
  signal?: AbortSignal;
}

export class MarketCatalogError extends Error {
  status: number;
  body: any;
  constructor(message: string, status: number, body: any) {
    super(message);
    this.name = "MarketCatalogError";
    this.status = status;
    this.body = body;
  }
}

export async function listMarketCatalog(opts: MarketCatalogOptions): Promise<MarketCatalogEntry[]> {
  const qs = opts.refresh ? "?refresh=1" : "";
  const resp = await fetch(`${opts.workerUrl}/api/markets/catalog${qs}`, {
    headers: { "X-View-Password": opts.viewPassword },
    signal: opts.signal,
  });
  let body: any = null;
  try { body = await resp.json(); } catch {}
  if (!resp.ok) {
    throw new MarketCatalogError(
      `listMarketCatalog failed: ${resp.status} ${body?.error || resp.statusText}`,
      resp.status,
      body,
    );
  }
  return Array.isArray(body) ? body : [];
}

// Phase 5.5e — detect markets from a HubSpot owner's deals.
export interface DetectedMarketEntry {
  slug: string;
  label: string;
  dealCount: number;
}

export interface DetectedMarketsOptions {
  workerUrl: string;
  viewPassword: string;
  /** Numeric HubSpot owner ID to scan. */
  ownerId: string;
  /** Force HubSpot re-fetch (bypasses worker's 6h per-owner KV cache). */
  refresh?: boolean;
  signal?: AbortSignal;
}

export async function detectMarketsForOwner(opts: DetectedMarketsOptions): Promise<DetectedMarketEntry[]> {
  const qs = new URLSearchParams({ ownerId: String(opts.ownerId) });
  if (opts.refresh) qs.set("refresh", "1");
  const resp = await fetch(`${opts.workerUrl}/api/markets/detected?${qs.toString()}`, {
    headers: { "X-View-Password": opts.viewPassword },
    signal: opts.signal,
  });
  let body: any = null;
  try { body = await resp.json(); } catch {}
  if (!resp.ok) {
    throw new MarketCatalogError(
      `detectMarketsForOwner failed: ${resp.status} ${body?.error || resp.statusText}`,
      resp.status,
      body,
    );
  }
  return Array.isArray(body) ? body : [];
}
